import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import Logo from '../components/Logo';
import BackgroundLayout from '../components/BackgroundLayout';
import Loader from '../components/Loader';

const ManageAppointmentPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [appointment, setAppointment] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRescheduling, setIsRescheduling] = useState(false);
    
    // Schedule state
    const [availableDates, setAvailableDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [selectedTime, setSelectedTime] = useState(null);
    const [isScheduleLoading, setIsScheduleLoading] = useState(false);

    dayjs.locale('es');

    useEffect(() => {
        const fetchAppointment = async () => {
            try {
                const response = await fetch(`https://admin-django-inspira.azurewebsites.net/api/services/manage-appointment/${token}/`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'No se pudo cargar la cita.');
                }
                const data = await response.json();
                setAppointment(data);
            } catch (error) {
                toast.error(error.message);
                // Optionally navigate somewhere or show error state
            } finally {
                setIsLoading(false);
            }
        };

        if (token) {
            fetchAppointment();
        }
    }, [token]);

    const handleCancel = async () => {
        if (!window.confirm('¿Estás segurx de querer cancelar tu cita?')) return;
        
        const toastId = toast.loading('Cancelando cita...');
        try {
            const response = await fetch(`https://admin-django-inspira.azurewebsites.net/api/services/manage-appointment/${token}/`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'cancel' }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al cancelar');
            }
            
            toast.success('Tu cita ha sido cancelada.', { id: toastId });
            navigate('/');
        } catch (error) {
            toast.error(error.message, { id: toastId });
        }
    };

    const fetchSchedule = async (serviceName) => {
        setIsScheduleLoading(true);
        let service_type = '';
        if (serviceName.includes('PREP') || serviceName.includes('DETECCIÓN') || serviceName.includes('AUTO PRUEBA') || serviceName.includes('CONSEJERIA')) {
            service_type = 'detection';
        } else {
            service_type = 'medical';
        }

        try {
            const response = await fetch('https://admin-django-inspira.azurewebsites.net/api/services/get-schedule/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ service_type }),
            });

            const data = await response.json();

            if (service_type === 'detection') {
                const parsedDates = Object.keys(data).map(dateStr => ({
                    date: dayjs(dateStr),
                    times: data[dateStr],
                }));
                setAvailableDates(parsedDates);
            } else if (service_type === 'medical' && data.next_available) {
                setAvailableDates([{ date: dayjs(data.next_available.date), times: [data.next_available.time] }]);
            } else {
                setAvailableDates([]);
            }
        } catch (error) {
            toast.error('Error al cargar horarios.');
        } finally {
            setIsScheduleLoading(false);
        }
    };

    const startRescheduling = () => {
        setIsRescheduling(true);
        if (appointment) {
            fetchSchedule(appointment.service);
        }
    };

    const handleDateClick = (dateObj) => {
        setSelectedDate(dateObj.date);
        setSelectedTime(null);
        setAvailableTimes(dateObj.times);
    };

    const handleTimeClick = (time) => {
        setSelectedTime(time);
    };

    const handleRescheduleSubmit = async () => {
        if (!selectedDate || !selectedTime) {
            toast.error('Por favor selecciona una fecha y un horario');
            return;
        }

        const toastId = toast.loading('Reprogramando tu cita...');
        
        try {
            const body = {
                action: 'reschedule',
                date: selectedDate.format('YYYY-MM-DD'),
                time: selectedTime,
            };

            const response = await fetch(`https://admin-django-inspira.azurewebsites.net/api/services/manage-appointment/${token}/`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al reprogramar');
            }
            
            toast.success('¡Cita reprogramada con éxito!', { id: toastId });
            navigate(`/thank-you?cita=${token}`);
        } catch (error) {
            toast.error(error.message, { id: toastId });
        }
    };

    if (isLoading) {
        return (
            <BackgroundLayout className="px-8 flex flex-col items-center justify-center">
                <Loader text="Cargando tu información..." />
            </BackgroundLayout>
        );
    }

    if (!appointment) {
        return (
            <BackgroundLayout className="px-8 flex flex-col items-center">
                <motion.div className="w-full text-center mt-20" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <Logo />
                    <h1 className="text-3xl font-bold text-gray-800 mt-10">Enlace inactivo 🤔</h1>
                    <p className="mt-4 text-gray-600">Este enlace pertenece a una cita que ya ha sido atendida, cancelada o el identificador no existe.</p>
                    <button onClick={() => navigate('/')} className="mt-8 p-3 bg-inspiraPink text-white rounded-md hover:bg-opacity-90 w-full lg:w-1/3">
                        Ir al inicio
                    </button>
                </motion.div>
            </BackgroundLayout>
        );
    }

    return (
        <BackgroundLayout className="px-8 flex flex-col items-center">
            <motion.div
                className="w-full text-left mt-0 lg:w-2/3 lg:mb-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="flex justify-center mb-8"><Logo /></div>
                
                <div className="bg-white/80 p-8 rounded-3xl shadow-lg border border-gray-100">
                    <h1 className='text-3xl lg:text-4xl font-bold text-[#f8485e] uppercase drop-shadow-sm mb-6 text-center'>
                        Gestionar Cita 🗓️
                    </h1>
                    
                    <div className="mb-8">
                        <p className="text-gray-600 mb-2 font-semibold">Tus próximos planes con nosotrxs:</p>
                        <div className="bg-[#f2f8fc] p-6 rounded-2xl border-l-4 border-[#f8485e]">
                            <p className="text-xl font-bold text-gray-800 mb-1">{appointment.service_display}</p>
                            <p className="text-gray-600 capitalize">
                                <strong>Fecha:</strong> {dayjs(appointment.date).format('dddd, D [de] MMMM YYYY')}
                            </p>
                            <p className="text-gray-600">
                                <strong>Hora:</strong> {appointment.time} hrs
                            </p>
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        {!isRescheduling ? (
                            <motion.div key="options" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col md:flex-row gap-4 mt-8">
                                <button
                                    onClick={startRescheduling}
                                    className="flex-1 py-4 px-6 bg-white border-2 border-[#f8485e] text-[#f8485e] font-bold rounded-2xl shadow-sm hover:shadow-md transition-all text-center"
                                >
                                    Reprogramar Cita
                                </button>
                                <button
                                    onClick={handleCancel}
                                    className="flex-1 py-4 px-6 bg-[#f8485e] text-white font-bold rounded-2xl shadow-md hover:shadow-lg transition-all text-center"
                                >
                                    Cancelar Cita
                                </button>
                            </motion.div>
                        ) : (
                            <motion.div key="calendar" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                                <hr className="my-6 border-gray-200" />
                                <h2 className="text-xl font-bold mt-5 mb-5 text-gray-800 uppercase drop-shadow-sm">
                                    Elige la nueva fecha:
                                </h2>
                                
                                {isScheduleLoading ? (
                                    <Loader text="Consultando fechas..." />
                                ) : (
                                    <>
                                        <div className="grid grid-cols-4 gap-3 lg:gap-4">
                                            {availableDates.map((dateObj, index) => (
                                                <button
                                                    key={index}
                                                    className={`p-3 border-2 rounded-xl text-sm md:text-base font-bold shadow-sm transition-all ${selectedDate && selectedDate.isSame(dateObj.date, 'day')
                                                        ? 'bg-[#f8485e] text-white border-[#f8485e]'
                                                        : 'bg-white text-[#f8485e] border-[#f8485e] hover:bg-[#f8485e]/10'
                                                    }`}
                                                    onClick={() => handleDateClick(dateObj)}
                                                >
                                                    {dateObj.date.format('DD ddd')}
                                                </button>
                                            ))}
                                        </div>

                                        {selectedDate && availableTimes.length > 0 && (
                                            <>
                                                <h2 className="text-xl font-bold mt-8 mb-5 text-gray-800 uppercase drop-shadow-sm">
                                                    Horarios disponibles:
                                                </h2>
                                                <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-5">
                                                    {availableTimes.map((time, index) => (
                                                        <button
                                                            key={index}
                                                            className={`p-3 border-2 rounded-xl text-sm md:text-base font-bold shadow-sm transition-all ${selectedTime === time
                                                                ? 'bg-[#f8485e] text-white border-[#f8485e]'
                                                                : 'bg-white text-[#f8485e] border-[#f8485e] hover:bg-[#f8485e]/10'
                                                            }`}
                                                            onClick={() => handleTimeClick(time)}
                                                        >
                                                            {time}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {selectedDate && selectedTime && (
                                            <div className='flex justify-center mt-10 gap-4'>
                                                <button
                                                    className="w-full py-4 bg-[#f8485e] text-white font-bold rounded-2xl shadow-md hover:shadow-lg transition-all"
                                                    onClick={handleRescheduleSubmit}
                                                >
                                                    Confirmar Reprogramación
                                                </button>
                                            </div>
                                        )}
                                        
                                        <div className='flex justify-center mt-4'>
                                            <button
                                                className="text-gray-500 font-semibold hover:text-gray-700 underline text-sm"
                                                onClick={() => setIsRescheduling(false)}
                                            >
                                                Volver a opciones
                                            </button>
                                        </div>
                                    </>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </BackgroundLayout>
    );
};

export default ManageAppointmentPage;
